import { BrokerDetailsGridData } from "./tpr.model";
import {
  getRatingBadgeDetails,
  getPositiveNegativeTextColor,
} from "./tpr-utils";
import {
  BROKER_DETAIL_GRID_COLUMN_MAPPING,
  BROKER_DETAIL_GRID_SORT_KEYS,
} from "./tpr-constants";
import { DatePipe } from "@angular/common";

export function displayNumbers(
  value,
  isCompanyAnnouncement,
  isPercentageValue = false,
  showPlusSign = false,
) {
  let valueToUse = value;
  const isNumber = typeof value === "number";
  if (isNumber) {
    valueToUse = new Intl.NumberFormat("en-US", {
      maximumFractionDigits: 2,
      minimumFractionDigits: 2,
    }).format(value);
  }
  let result = valueToUse;
  if (!isCompanyAnnouncement) {
    if (isNumber) {
      if (showPlusSign && value > 0) {
        result = `+${valueToUse}`;
      }
      if (isPercentageValue) {
        result += "%";
      }
    } else {
      result = "-";
    }
  }
  return result;
}

export function mapBrokerDetailsGridData(
  values,
  actionHandlers,
  currenySymbol = "",
) {
  const result = {};
  for (const key in values) {
    if (Object.prototype.hasOwnProperty.call(values, key)) {
      const item = values[key];
      const dataObject: BrokerDetailsGridData = {
        id: key,
      };
      if (item.broker) {
        dataObject.broker = {
          target: "_blank",
          displayText: item.broker,
          tooltipText: item.broker_name_clickable ? item.broker : undefined,
          url: item.broker_name_clickable
            ? `/reporting/broker/${item.broker}`
            : "",
        };
      }
      if (item.analyst) {
        if (item.link_clickable) {
          dataObject.analyst = {
            target: "_blank",
            displayText: item.analyst,
            tooltipText: "",
            url: `/reporting/analyst/${item.analyst_id}`,
            textEllipsis: 23,
            name: item.analyst,
            broker: item.broker,
            email: item.analyst_email,
            nameLink: `/reporting/analyst/${item.analyst_id}`,
            brokerLink: `/reporting/broker/${item.broker}`,
            emailClickCallback: actionHandlers.analystEmailClick,
            emailClickData: {
              broker: item.broker,
              analyst: item.analyst,
              email: item.analyst_email,
            },
          };
        } else {
          dataObject.analyst = {
            displayText: item.analyst,
            tooltipText: undefined,
            url: "",
            textEllipsis: 22,
          };
        }
      }
      if (item.publish_date) {
        let tootlTipText = "";
        if (!item.company_announcements) {
          tootlTipText = `Last change: ${new DatePipe("en-US").transform(
            item.publish_date,
            "dd-MMM-yy",
          )} (${item.daydiff})`;
        } else if (item.statedperiod) {
          tootlTipText = item.statedperiod;
        }
        dataObject.publish_date = {
          dateValue: item.publish_date,
          dayDiff: item.daydiff,
          iconColor: "#1874AE ",
          icon: item.guidance ? "guidance" : undefined,
          textTooltip: tootlTipText,
          iconTooltip: item.publish_date
            ? item.guidance
              ? "Guidance is available in this document, click on Filings to view"
              : `This model was republished on ${new Date(
                  item.publish_date,
                ).toDateString()}`
            : undefined,
          clickData: {
            broker: item.broker,
            id: key,
            statedperiod: item.statedperiod,
          },
        };
      }
      if (item.status) {
        const isNotCurrentStatus = item.status === "Not Current";
        const statusDetails = {
          "Not Current": {
            icon: "close-circle",
            color: "grey",
          },
          "In Progress": {
            icon: "sync",
            color: "#1874ae",
          },
        };
        dataObject.status = {
          linkText: item.status,
          isTextNotLink: true,
          show: true,
          iconColor: statusDetails[item.status]
            ? statusDetails[item.status].color
            : "white",
          icon: statusDetails[item.status]
            ? statusDetails[item.status].icon
            : undefined,
          iconSize: "small",
          iconPosition: "end",
          iconTooltipHeader: item.status_title || "",
          iconTooltip: item.status_message || "",
          email: isNotCurrentStatus ? item.analyst_email : "",
          clickData: {
            broker: item.broker,
            id: key,
          },
        };
      }
      dataObject.broker_rating = {
        text: item.broker_rating,
        clickData: {
          broker: item.broker,
          id: key,
        },
      };
      if (item.normalized_rating) {
        const { text, bgColor, textColor, id } = getRatingBadgeDetails(
          item.normalized_rating,
        );
        dataObject.normalized_rating = {
          text: text,
          textColor: textColor,
          badgeColor: bgColor,
          id,
          raw: item.normalized_rating,
        };
      }
      const targetPrice = item.target_price
        ? currenySymbol +
          displayNumbers(item.target_price, item.company_announcements)
        : displayNumbers(item.target_price, item.company_announcements);

      dataObject.target_price = {
        linkText: targetPrice,
        raw: item.target_price,
        isTextNotLink: true,
        show: true,
        cellId: `${key}-target_price-${item.target_price}`,
        iconColor: "#667284",
        icon: item.show_ca_icon ? "corporate-action" : undefined,
        iconSize: "small",
        iconPosition: "end",
        iconTooltipHeader: "Corporate Action",
        iconTooltip: `Adjusted: ${targetPrice} <br> Unadjusted: ${currenySymbol}${displayNumbers(
          item.original_target_price,
          item.company_announcements,
        )}`,
        clickCallback: actionHandlers.handleDPCellClick,
        clickData: {
          broker: item.broker,
          id: key,
          source_id: item.source_id,
          value: targetPrice,
          dataExist: item.target_price,
          dp: item.dp ? item.dp.target_price : undefined,
          cellId: `${key}-target_price-${item.target_price}`,
          rowId: key,
          columnLabel: BROKER_DETAIL_GRID_COLUMN_MAPPING["target_price"].label,
          partial_entitled_sid: item.partial_entitled_sid,
        },
      };
      const impliedReturn = displayNumbers(
        item.implied_return,
        item.company_announcements,
        true,
        true,
      );
      dataObject.implied_return = {
        text: impliedReturn,
        textColor: getPositiveNegativeTextColor(item.implied_return),
        raw: item.implied_return,
        cellId: `${key}-implied_return-${item.implied_return}`,
        clickCallback: actionHandlers.handleDPCellClick,
        clickData: {
          broker: item.broker,
          id: key,
          source_id: item.source_id,
          value: impliedReturn,
          dataExist: typeof item.implied_return === "number",
          dp: item.dp ? item.dp.implied_return : undefined,
          cellId: `${key}-implied_return-${item.implied_return}`,
          rowId: key,
          columnLabel:
            BROKER_DETAIL_GRID_COLUMN_MAPPING["implied_return"].label,
          partial_entitled_sid: item.partial_entitled_sid,
        },
      };
      const currentImpliedReturn = displayNumbers(
        item.current_implied_return,
        item.company_announcements,
        true,
        true,
      );
      dataObject.current_implied_return = {
        text: currentImpliedReturn,
        textColor: getPositiveNegativeTextColor(item.current_implied_return),
        raw: item.current_implied_return,
        cellId: `${key}-current_implied_return-${item.current_implied_return}`,
        clickCallback: actionHandlers.handleDPCellClick,
        clickData: {
          broker: item.broker,
          id: key,
          source_id: item.source_id,
          value: currentImpliedReturn,
          dataExist: typeof item.current_implied_return === "number",
          dp: item.dp ? item.dp.current_implied_return : undefined,
          cellId: `${key}-current_implied_return-${item.current_implied_return}`,
          rowId: key,
          columnLabel:
            BROKER_DETAIL_GRID_COLUMN_MAPPING["current_implied_return"].label,
          partial_entitled_sid: item.partial_entitled_sid,
        },
      };

      if (!item.company_announcements) {
        dataObject.documents = {
          show: true,
          iconColor: item.document_clickable ? "#49525F" : "#D3D7DD",
          title: item.document_clickable
            ? "Click to view supporting documentation"
            : "Not entitled to supporting documentation",
          icon: "folder",
          clickData: {
            broker: item.broker,
            id: key,
            source_id: item.source_id,
            document_clickable: item.document_clickable,
          },
          clickCallback: actionHandlers.handleCompanyDocumentClick,
        };
      } else {
        dataObject.documents = {
          show: true,
          clickData: {
            broker: item.broker,
            id: key,
            documentId: item.document_id,
            documentType: item.document_type,
            fileType: item.file_type,
          },
          linkText: "Filings",
          clickCallback: actionHandlers.handleCompanyFillingClick,
        };
      }
      dataObject.broker_rating_action = {
        text: item.broker_rating_action,
        clickData: {
          broker: item.broker,
          id: key,
        },
      };
      const priceAtDatePublished = item.price_at_date_published
        ? currenySymbol +
          displayNumbers(
            item.price_at_date_published,
            item.company_announcements,
          )
        : displayNumbers(
            item.price_at_date_published,
            item.company_announcements,
          );
      dataObject.price_at_date_published = {
        text: priceAtDatePublished,
        cellId: `${key}-price_at_date_published-${item.price_at_date_published}`,
        clickCallback: actionHandlers.handleDPCellClick,
        clickData: {
          broker: item.broker,
          id: key,
          source_id: item.source_id,
          value: priceAtDatePublished,
          dataExist: item.price_at_date_published,
          dp: item.dp ? item.dp.price_at_date_published : undefined,
          cellId: `${key}-price_at_date_published-${item.price_at_date_published}`,
          rowId: key,
          columnLabel:
            BROKER_DETAIL_GRID_COLUMN_MAPPING["price_at_date_published"].label,
          partial_entitled_sid: item.partial_entitled_sid,
        },
        raw: item.price_at_date_published,
      };
      const realizedReturn = displayNumbers(
        item.realized_return,
        item.company_announcements,
        true,
        true,
      );
      dataObject.realized_return = {
        text: realizedReturn,
        clickCallback: actionHandlers.handleDPCellClick,
        cellId: `${key}-realized_return-${item.realized_return}`,
        clickData: {
          broker: item.broker,
          id: key,
          source_id: item.source_id,
          value: realizedReturn,
          dataExist: typeof item.realized_return === "number",
          dp: item.dp ? item.dp.realized_return : undefined,
          cellId: `${key}-realized_return-${item.realized_return}`,
          rowId: key,
          columnLabel:
            BROKER_DETAIL_GRID_COLUMN_MAPPING["realized_return"].label,
          partial_entitled_sid: item.partial_entitled_sid,
        },
        textColor: getPositiveNegativeTextColor(item.realized_return),
        raw: item.realized_return,
      };
      const unrealizedReturn = displayNumbers(
        item.unrealized_return,
        item.company_announcements,
        true,
        true,
      );
      dataObject.unrealized_return = {
        text: unrealizedReturn,
        clickCallback: actionHandlers.handleDPCellClick,
        cellId: `${key}-unrealized_return-${item.unrealized_return}`,
        clickData: {
          broker: item.broker,
          id: key,
          source_id: item.source_id,
          value: unrealizedReturn,
          dataExist: typeof item.unrealized_return === "number",
          dp: item.dp ? item.dp.unrealized_return : undefined,
          cellId: `${key}-unrealized_return-${item.unrealized_return}`,
          rowId: key,
          columnLabel:
            BROKER_DETAIL_GRID_COLUMN_MAPPING["unrealized_return"].label,
          partial_entitled_sid: item.partial_entitled_sid,
        },
        textColor: getPositiveNegativeTextColor(item.unrealized_return),
        raw: item.unrealized_return,
      };

      // row object is ready now
      result[key] = dataObject;
    }
  }
  return result;
}

export function calculateAverageAndMedian(
  averageData,
  medianData,
  normalizedRatingFilterSelection,
  currenySymbol = "",
) {
  const ratingMaping = {
    "-1": "total",
    1: "buy",
    2: "hold",
    3: "sell",
  };
  const targetPriceAverageResult =
    averageData.target_price[ratingMaping[normalizedRatingFilterSelection]];
  const targetPriceMedianResult =
    medianData.target_price[ratingMaping[normalizedRatingFilterSelection]];
  const currentImpliedReturnAverageResult =
    averageData.current_implied_return[
      ratingMaping[normalizedRatingFilterSelection]
    ];
  const currentImpliedReturnMedianResult =
    medianData.current_implied_return[
      ratingMaping[normalizedRatingFilterSelection]
    ];
  const medianItem = {
    broker: {
      displayText: "Median",
      tooltipText: undefined,
      url: "",
    },
    target_price: {
      linkText: targetPriceMedianResult
        ? currenySymbol + displayNumbers(targetPriceMedianResult, false)
        : displayNumbers(targetPriceMedianResult, false),
      raw: targetPriceMedianResult,
      show: true,
      isTextNotLink: true,
    },

    current_implied_return: {
      text: displayNumbers(
        currentImpliedReturnMedianResult,
        false,
        true,
        false,
      ),
      raw: currentImpliedReturnMedianResult,
    },
  };
  const averageItem = {
    broker: {
      displayText: "Average",
      tooltipText: undefined,
      url: "",
    },
    target_price: {
      linkText: targetPriceAverageResult
        ? currenySymbol + displayNumbers(targetPriceAverageResult, false)
        : displayNumbers(targetPriceAverageResult, false),
      raw: targetPriceAverageResult,
      show: true,
      isTextNotLink: true,
    },

    current_implied_return: {
      text: displayNumbers(
        currentImpliedReturnAverageResult,
        false,
        true,
        false,
      ),
      raw: currentImpliedReturnAverageResult,
    },
  };
  return { averageItem, medianItem };
}

export function createBrokerDetailsGridData(
  keys,
  gridDataMapping,
  normalizedRatingFilterSelection,
  averageData,
  medianData,
  currenySymbol = "",
) {
  const result = [];
  if (keys) {
    for (const key of keys) {
      const item = gridDataMapping[key];
      if (
        item &&
        (normalizedRatingFilterSelection === -1 ||
          (item.normalized_rating &&
            item.status &&
            item.status.linkText === "Current" &&
            item.normalized_rating.id === normalizedRatingFilterSelection))
      ) {
        result.push(item);
      }
    }
    if (result && result.length && averageData && medianData) {
      const { averageItem, medianItem } = calculateAverageAndMedian(
        averageData,
        medianData,
        normalizedRatingFilterSelection,
        currenySymbol,
      );

      result.push(averageItem, medianItem);
    }
  }

  return result;
}

export function getBrokerDetailsGridFields(columns) {
  return columns.map((item) => ({
    renderer: BROKER_DETAIL_GRID_COLUMN_MAPPING[item].renderer,
    field: BROKER_DETAIL_GRID_COLUMN_MAPPING[item].id,
    alignment: BROKER_DETAIL_GRID_COLUMN_MAPPING[item].cellTextAlignment,
  }));
}

export function getBrokerDetailsGridColumnData(columns?) {
  return [
    columns.map((item) => ({
      header: BROKER_DETAIL_GRID_COLUMN_MAPPING[item].label,
      sortKey: BROKER_DETAIL_GRID_COLUMN_MAPPING[item].id,
      showSort: BROKER_DETAIL_GRID_COLUMN_MAPPING[item].showSort,
      children: 0,
    })),
  ];
}

export function getBrokerDetailsColumnWidth(keyArray) {
  return keyArray.map((item) => BROKER_DETAIL_GRID_COLUMN_MAPPING[item].width);
}

export function getSortingDetails(values) {
  const result = {};
  for (const item of BROKER_DETAIL_GRID_SORT_KEYS) {
    result[item] = {
      ascend: values[`asc_${item}`],
      descend: values[`desc_${item}`],
    };
  }
  return result;
}

export function getSortesArrayKeys(key, order, sortingDetails) {
  if (sortingDetails) {
    return sortingDetails[key][order] || sortingDetails["default"]["descend"];
  }
  return [];
}
